import { config } from '../config';
import { authorizedFetch } from './auth';

/**
 * Label PDFs are fetched as blobs rather than linked directly: the endpoint needs
 * a bearer token, so a plain <iframe src> or <a href> would get a 401. The blob
 * URL is what the viewer renders.
 */

export type LabelFormat = 'A4' | 'A6';

/** Pulls shipment ids out of a tool result so the UI can offer the label. */
export function shipmentIdsFromToolResult(text: string): number[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return [];
  }

  const record = parsed as Record<string, unknown>;
  const ids: number[] = [];

  if (typeof record['shipment_id'] === 'number') {
    ids.push(record['shipment_id']);
  }

  if (Array.isArray(record['shipment_ids'])) {
    for (const id of record['shipment_ids']) {
      if (typeof id === 'number') {
        ids.push(id);
      }
    }
  }

  return [...new Set(ids)].filter((id) => Number.isSafeInteger(id) && id > 0);
}

export class LabelNotReadyError extends Error {}

/**
 * Reads the handler's error body. `detail` carries the actual upstream response when
 * the handler could not make sense of it — dropping that here would put the shape
 * back out of reach, which is the whole reason the handler reports it.
 */
async function readError(response: Response): Promise<{ error: string; detail?: unknown }> {
  try {
    const body = (await response.json()) as { error?: string; detail?: unknown };
    return { error: body.error ?? `HTTP ${response.status}`, detail: body.detail };
  } catch {
    return { error: `HTTP ${response.status}` };
  }
}

export async function fetchLabelBlobUrl(
  shipmentIds: number[],
  format: LabelFormat = 'A6',
): Promise<string> {
  const path = `${config().apiBase}/labels/${shipmentIds.join(';')}?format=${format}`;
  const response = await authorizedFetch(path, { headers: { Accept: 'application/pdf' } });

  if (!response.ok) {
    const { error, detail } = await readError(response);

    // 409 is the API's 404 in disguise: the label may still be rendering, but an
    // unknown shipment looks identical from here. Offer the retry without claiming
    // which of the two it is.
    if (response.status === 409) {
      throw new LabelNotReadyError(`${error} Probeer het over een paar seconden opnieuw.`);
    }

    throw new Error(detail === undefined ? error : `${error} ${JSON.stringify(detail)}`);
  }

  return URL.createObjectURL(await response.blob());
}
